// Settings card (office and admin, Session AUF, Director decision DL-2): the recommended examining body.
//
// The ONE examining body a customer with no examiner is referred to by name, directly ("it works out cheaper for you going
// to them direct than through us", "mention East London Elevators when you book", "send us a copy of the report"), in the
// LOLER reminder and in replies the email engine drafts. Only ever to a customer we do NOT arrange examinations for, and no
// other examining body is ever named. The default is E.L.T.I. (UK) Ltd; "Use the default" puts it back. Every change is
// previewed, then saved through a two-step confirm; audited server side.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { RecommendedExaminerSettings } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { formatDateTime } from "../../lib/format";
import { Loader } from "../../components/Loader";

type Change = { name: string; email: string } | { useDefault: true };
type Pending = { change: Change; referralText: string; ok: boolean; refusals: string[]; isDefault: boolean };

const linkBtn = "font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const fieldCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 font-body text-sm text-ele-strong outline-none focus:border-ele-green";

export function RecommendedExaminerSettingsCard({ refreshKey }: { refreshKey?: number }) {
  const [view, setView] = useState<RecommendedExaminerSettings | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState<"preview" | "save" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setState("loading");
    Promise.resolve()
      .then(() => api.settings.recommendedExaminer.get())
      .then((v) => {
        if (!live) return;
        setView(v);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  async function preview(change: Change) {
    setBusy("preview");
    setError(null);
    setMessage(null);
    setPending(null);
    try {
      const res = await api.settings.recommendedExaminer.put({ ...change, preview: true });
      if (res.preview) setPending({ change, referralText: res.referralText, ok: res.ok, refusals: res.refusals, isDefault: res.isDefault });
    } catch (e) {
      setError(friendlyApiError(e, "Could not check the examining body. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!pending?.ok) return;
    setBusy("save");
    setError(null);
    try {
      const res = await api.settings.recommendedExaminer.put({ ...pending.change, preview: false });
      if (!res.preview) {
        setView(res);
        setMessage("Saved. Later reminders and replies refer customers with no examiner to it.");
        setPending(null);
        setEditing(false);
      }
    } catch (e) {
      setError(friendlyApiError(e, "Could not save the examining body. Please try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Recommended examining body">
      <h2 className="mb-2 font-heading text-lg text-ele-blue">Recommended examining body</h2>
      <p className="mb-3 text-sm text-ele-body">
        A customer who arranges their own LOLER examinations and has no examiner is referred to this one body by name, to book direct. It is never
        named to a customer we arrange examinations for, and no other examining body is ever named.
      </p>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !view ? (
        <p role="alert" className="text-sm text-ele-strong">
          Could not load the examining body.
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ele-strong" data-testid="recommended-examiner-current">
            {view.referralText}
          </p>
          <p className="text-xs text-ele-body">
            {view.isDefault ? "This is the default." : "This is the office's own choice."}
            {view.updatedAt ? ` Last changed ${formatDateTime(view.updatedAt)}${view.updatedByName ? ` by ${view.updatedByName}` : ""}.` : ""}
          </p>
          {!editing ? (
            <button
              type="button"
              className={linkBtn}
              onClick={() => {
                setName(view.name);
                setEmail(view.email);
                setEditing(true);
                setPending(null);
              }}
            >
              Change the examining body
            </button>
          ) : (
            <div className="space-y-2">
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="block">
                  <span className={labelCls}>Name</span>
                  <input aria-label="Examining body name" value={name} maxLength={120} onChange={(e) => { setName(e.target.value); setPending(null); }} className={fieldCls} />
                </label>
                <label className="block">
                  <span className={labelCls}>Email</span>
                  <input aria-label="Examining body email" value={email} maxLength={200} onChange={(e) => { setEmail(e.target.value); setPending(null); }} className={fieldCls} />
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => void preview({ name, email })}
                  disabled={busy != null || !name.trim() || !email.trim()}
                  className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-strong transition hover:text-ele-green disabled:opacity-50"
                >
                  {busy === "preview" ? "Checking..." : "Preview"}
                </button>
                {!view.isDefault && (
                  <button type="button" className={linkBtn} disabled={busy != null} onClick={() => void preview({ useDefault: true })}>
                    Use the default
                  </button>
                )}
                <button type="button" className={linkBtn} onClick={() => { setEditing(false); setPending(null); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {pending && (
            <div className="rounded-ele border border-ele-hairline p-3" data-testid="recommended-examiner-preview">
              <p className="text-sm text-ele-strong">Customers with no examiner will be referred to: {pending.referralText}</p>
              {pending.refusals.length > 0 ? (
                <ul role="alert" className="mt-2 space-y-1 text-sm text-ele-danger">
                  {pending.refusals.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              ) : (
                <div className="mt-2">
                  <ConfirmButton
                    onConfirm={() => void save()}
                    label={pending.isDefault ? "Use the default" : "Save this examining body"}
                    confirmLabel="Later reminders and replies will name it. Press again"
                    busy={busy === "save"}
                    busyLabel="Saving..."
                    disabled={busy != null}
                    className="rounded-ele border border-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-green"
                    armedClassName="!text-ele-green font-semibold"
                  />
                </div>
              )}
            </div>
          )}
          {error && (
            <p role="alert" className="text-sm text-ele-danger">
              {error}
            </p>
          )}
          {message && (
            <p role="status" className="text-sm text-ele-strong">
              {message}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
